import { FastForward, Pause, Play, Rewind, type LucideIcon } from "lucide-react";
import * as React from "react";
import Linkify from "react-linkify";

import { cn } from "@/lib/utils";

export function ShortcutRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <li className="flex items-center justify-between gap-3">
      <span>{label}</span>
      <span className="inline-flex items-center gap-1">{children}</span>
    </li>
  );
}

export function PlayerInfoRow({
  icon: Icon,
  title,
  children,
}: {
  icon: LucideIcon;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div
      className="flex items-start gap-2 rounded-lg border border-slate-200/40 bg-white px-2.5 py-1.5 dark:border-slate-700/50 dark:bg-slate-900"
      aria-label={title}
    >
      <Icon className="mt-0.5 size-4 shrink-0 text-slate-400 dark:text-slate-500" aria-hidden />
      <div className="min-w-0 font-medium text-slate-700 dark:text-slate-200">{children}</div>
    </div>
  );
}

export function HudCard({
  title,
  icon: Icon,
  iconClass,
  active,
  action,
  children,
}: {
  title: string;
  icon: LucideIcon;
  iconClass: string;
  active?: boolean;
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section className="flex flex-col gap-3">
      <div className="flex flex-row items-center justify-between w-full select-none">
        <label className="sectionHeader flex items-center gap-1.5 text-sm font-semibold text-slate-800 dark:text-slate-100">
          <Icon className={cn("size-4.5", iconClass)} />
          {title}
        </label>
        {action}
      </div>
      <div
        className={cn(
          "videoProjectContainer flex flex-col gap-3 rounded-xl border border-slate-200 bg-slate-50/50 p-4 text-xs dark:border-slate-700 dark:bg-slate-900/70",
          active && "ring-2 ring-amber-400"
        )}
      >
        {children}
      </div>
    </section>
  );
}

export function LinkifiedText({ children }: { children: string }) {
  return (
    <span className="whitespace-pre-wrap wrap-break-word">
      <Linkify
        componentDecorator={(href, text, key) => {
          const truncatedText = text.length > 30 ? `${text.substring(0, 27)}...` : text;
          return (
            <a
              key={key}
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              title={text}
              className="text-primary hover:underline"
            >
              {truncatedText}
            </a>
          );
        }}
      >
        {children}
      </Linkify>
    </span>
  );
}

function FadeCue({ align = "center", children }: { align?: "left" | "center" | "right"; children: React.ReactNode }) {
  const [phase, setPhase] = React.useState<"start" | "shown" | "gone">("start");

  React.useEffect(() => {
    const show = window.requestAnimationFrame(() => setPhase("shown"));
    const hide = window.setTimeout(() => setPhase("gone"), 350);
    return () => {
      window.cancelAnimationFrame(show);
      window.clearTimeout(hide);
    };
  }, []);

  return (
    <div
      className={cn(
        "pointer-events-none absolute inset-0 z-10 flex items-center",
        align === "left" && "justify-start pl-[12%]",
        align === "center" && "justify-center",
        align === "right" && "justify-end pr-[12%]"
      )}
    >
      <div
        className={cn(
          "transition-all ease-out",
          phase === "start" && "scale-75 opacity-0 duration-150",
          phase === "shown" && "scale-100 opacity-100 duration-150",
          phase === "gone" && "scale-125 opacity-0 duration-400"
        )}
      >
        {children}
      </div>
    </div>
  );
}

export function PlaybackCue({ playing }: { playing: boolean }) {
  const Icon = playing ? Play : Pause;
  return (
    <FadeCue>
      <div className="flex size-16 items-center justify-center rounded-full bg-black/55 text-white shadow-lg">
        <Icon className="size-8 fill-white" />
      </div>
    </FadeCue>
  );
}

export function SkipCue({ seconds }: { seconds: number }) {
  const shown = Math.round(seconds);
  if (shown === 0) return null;
  const rewind = shown < 0;
  const label = rewind ? `${shown}` : `+${shown}`;
  const Icon = rewind ? Rewind : FastForward;
  return (
    <FadeCue align={rewind ? "left" : "right"}>
      <div className="flex items-center gap-1.5 rounded-full bg-black/55 px-3 py-2.5 text-white shadow-lg">
        {rewind ? <Icon className="size-5 fill-white" /> : null}
        <span className="text-lg font-semibold tracking-tight">{label}</span>
        {rewind ? null : <Icon className="size-5 fill-white" />}
      </div>
    </FadeCue>
  );
}
